/* eslint-disable no-nested-ternary */
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { colors } from '@EVOCORP/corechat-theme';
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Container,
  Fade,
  IconButton,
  ListItem,
  ListItemIcon,
  Paper,
  Popover,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Zoom,
} from '@material-ui/core';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import DoneIcon from '@material-ui/icons/Done';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import EmojiEmotionsOutlinedIcon from '@material-ui/icons/EmojiEmotionsOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import SmartphoneIcon from '@material-ui/icons/Smartphone';
import SpeakerNotesOutlinedIcon from '@material-ui/icons/SpeakerNotesOutlined';
import clsx from 'clsx';
import EmojiPicker, { EmojiStyle, Theme } from 'emoji-picker-react';
import {
  bindMenu,
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useModal } from 'react-modal-hook';
import { useDispatch } from 'react-redux';

import MensagemMidiaRemovida from 'components/MensagemMidiaRemovida';
import * as socketEvents from 'constants/socketEvents';
import dayjs from 'services/dayjs';
import socket from 'services/socket';
import { useAppSelector } from 'store';
import {
  atendimentosSelector,
  StatusSessaoEnum,
} from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';
import {
  MensagemInterface,
  reagirMensagem,
  TiposEventoMensagemEnum,
  TiposMensagemEnum,
} from 'store/reducers/mensagens';

import { categories } from './EmojiButton';
import ModalReacoes from './ModalReacoes';
import NewResponseMessage from './NewResponseMessage';
import ResponseMessage from './ResponseMessage';

export declare interface MessageBaseProps {
  fromMe?: boolean;
  mensagem: MensagemInterface;
  mensagemCitada?: MensagemInterface;
  verMensagemCitadaNoModal?: boolean; // variavel temporaria
  isMensagemPesquisada?: boolean;
  historicoAtendimento?: boolean;
  autor?: string;
  autorColor?: string;
  encaminhada?: boolean;
  isGroup?: boolean;
  apagada?: boolean;
  actions?: ReactNode[] | false;
  maxWidth?: string | number;
  width?: string | number;
  onReenviarMensagem?: () => void;
  children?: ReactNode;
  onVerHistoricoMensagemEditada?: () => void;
  onClick?: () => void;
}

const useStyles = makeStyles(({ palette, spacing, typography }) => ({
  root: ({ fromMe, mensagem: { evento, tipo } }: MessageBaseProps) => {
    const isSummaryIA = tipo === TiposMensagemEnum.RESUMO_IA;
    const justify = fromMe ? 'flex-end' : 'flex-start';

    return {
      justifyContent: evento || isSummaryIA ? 'center' : justify,
    };
  },
  container: {
    margin: 0,
    width: ({ width }: MessageBaseProps) => width ?? 'auto',
    position: 'relative',
    marginBlock: ({ mensagem: { evento, tipo } }: MessageBaseProps) => {
      const isSummaryIA = tipo === TiposMensagemEnum.RESUMO_IA;

      const isDataMensagens =
        evento?.tipo === TiposEventoMensagemEnum.DATA_MENSAGENS;

      if (isSummaryIA) {
        return spacing(2);
      }
      if (isDataMensagens) {
        return spacing(2);
      }
      return 0;
    },

    // por padrão o container assume o tamanho maximo de 336 pixels
    maxWidth: ({ maxWidth }: MessageBaseProps) => maxWidth ?? '336px',
  },
  rootNaoIniciaAtendimento: {
    // espaço para o ícone posicionado à esquerda do balão
    paddingLeft: spacing(5),
  },
  containerNaoIniciaAtendimento: {
    minWidth: 64,

    '& > header, & > footer': {
      opacity: 0.7,
    },
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    justifyContent: 'space-between',
    color: palette.text.primary,
  },
  contentMessage: ({
    fromMe,
    onClick,
    mensagem: { evento, tipo },
  }: MessageBaseProps) => {
    const isSummaryIA = tipo === TiposMensagemEnum.RESUMO_IA;
    const tiposDeMensagensInterativas = [
      TiposMensagemEnum.WPP_LIST,
      TiposMensagemEnum.WPP_REPLY_BTN,
    ];

    let bg = palette.background.tertiary;
    if (isSummaryIA) {
      bg = '#BFBFFF';
    } else if (fromMe) {
      bg = colors.light.primary.dark;
    }
    const color = palette.getContrastText(bg);
    const borderRadius = fromMe ? spacing(1, 1, 0, 1) : spacing(0, 1, 1, 1);

    return {
      overflow: 'hidden',
      position: 'relative',
      padding: tiposDeMensagensInterativas.includes(tipo) ? 0 : spacing(1),
      color: evento ? palette.text.primary : color,
      border: '1px solid transparent',
      backgroundColor: evento
        ? alpha(palette.action.disabledBackground, 0.05)
        : bg,
      borderRadius: evento || isSummaryIA ? spacing(1) : borderRadius,
      cursor: onClick ? 'pointer' : 'default',
    };
  },
  contentMessageNaoIniciaAtendimento: ({
    fromMe,
    mensagem: { evento, tipo },
  }: MessageBaseProps) => {
    let bg = palette.background.tertiary;
    if (tipo === TiposMensagemEnum.RESUMO_IA) {
      bg = '#BFBFFF';
    } else if (fromMe) {
      bg = colors.light.primary.dark;
    }
    const color = evento ? palette.text.primary : palette.getContrastText(bg);

    return {
      border: `2px dotted ${alpha(color, 0.1)} !important`,
    };
  },
  iconNaoIniciaAtendimento: {
    // centralizado verticalmente em relação ao balão
    position: 'absolute',
    right: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    marginRight: spacing(1),
    // mesma cor/opacidade da borda pontilhada do balão
    opacity: 0.9,
    color: alpha(palette.text.primary, 0.8),
    cursor: 'help',
  },
  contentMessageApagada: {
    opacity: 0.65,
    border: `1px solid ${palette.error.main}`,
    color: `${palette.text.primary} !important`,
    backgroundColor: `${alpha(palette.error.main, 0.1)} !important`,
  },
  contentMessageRemovida: ({ mensagem }: MessageBaseProps) => ({
    padding: 0,
    color: `${palette.text.primary} !important`,
    border: `1px dashed ${alpha(
      palette.error.main,
      mensagem.midiaTitulo ? 0.25 : 0.4,
    )}`,
    backgroundColor: 'transparent',
  }),
  contentMessagePesquisada: {
    animation: '$pulse 1.2s ease-in-out',
  },
  rootMensagemPesquisada: {
    animation: '$pulse 1.6s ease-in-out',
    animationFillMode: 'none',
    borderRadius: 'inherit',
  },
  '@keyframes pulse': {
    '0%': {},
    '10%': {
      backgroundColor: alpha(palette.primary.main, 0.12),
    },
    '50%': {
      backgroundColor: alpha(palette.primary.main, 0.08),
    },
    '90%': {
      backgroundColor: 'transparent',
    },
    '100%': {
      backgroundColor: 'transparent',
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    margin: '4px 0 -5px 4px',
    userSelect: 'none',
    fontSize: typography.pxToRem(10),
  },
  flexEndFooter: {
    justifyContent: 'flex-end',
  },
  chip: ({ fromMe }: MessageBaseProps) => {
    const bg = fromMe ? colors.light.primary.dark : palette.background.tertiary;

    return {
      backgroundColor: bg,
      borderRadius: '16px',
      border: `1px solid ${alpha(palette.text.primary, 0.15)}`,
      color: palette.getContrastText(bg),

      '&:hover': {
        backgroundColor: bg,
      },

      '&:active': {
        backgroundColor: bg,
      },

      '&:focus': {
        backgroundColor: bg,
      },
    };
  },
  paper: {
    width: 350,
    background: 'none',
  },
  loveEmoji: {
    fontSize: 24,
    cursor: 'pointer',
    transition: 'font-size 0.2s',

    '&:hover': {
      fontSize: 26,
      transition: 'font-size 0.2s',
    },
  },
  instagramPopoverPaper: {
    width: 150,
    height: 50,
    overflow: 'hidden',
    backgroundColor: 'none',
    padding: spacing(1, 2),
    borderRadius: spacing(10),
  },
  tooltipText: {
    fontSize: typography.pxToRem(12),
  },
}));

export default function MessageBase(props: MessageBaseProps) {
  const {
    fromMe,
    mensagem,
    mensagemCitada,
    apagada,
    autor,
    autorColor,
    encaminhada,
    actions,
    isMensagemPesquisada,
    verMensagemCitadaNoModal,
    historicoAtendimento,
    onReenviarMensagem,
    onVerHistoricoMensagemEditada,
    onClick,
    isGroup,
    children,
  } = props;
  const {
    evento,
    ackStatus,
    ackMotivoErro,
    ackDescricaoErro,
    criadoEm,
    isEditada,
    removidoPorAdminEm,
    tipo,
    reacoesEmojis,
    reacoesTotal,
    naoIniciaAtendimento,
  } = mensagem;

  const ackMetaError = ackMotivoErro === 'ACK_META_ERROR';
  const isSummaryIA = tipo === TiposMensagemEnum.RESUMO_IA;
  const theme = useTheme();
  const classes = useStyles(props);
  const dispatch = useDispatch();
  const [reenviando, setReenviando] = useState(false);
  const [reenviado, setReenviado] = useState(false);
  const [showMenuButton, setShowMenuButton] = useState(false);
  const [showButtonReacoes, setShowButtonReacoes] = useState(false);
  const [showMensagemPesquisada, setShowMensagemPesquisada] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (isMensagemPesquisada) {
      setShowMensagemPesquisada(true);

      timeoutId = setTimeout(() => {
        setShowMensagemPesquisada(false);
      }, 1700);
    }

    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [isMensagemPesquisada]);

  const {
    historicoAtendimentoSelecionado,
    historicoAtendimentoNaFila,
    atendimentoSelecionado,
  } = useAppSelector(atendimentosSelector);

  const isOpenSession =
    atendimentoSelecionado?.sessaoStatus === StatusSessaoEnum.OPEN ||
    atendimentoSelecionado?.sessaoStatus === undefined;

  const isInstagramOrMessengerChannel =
    atendimentoSelecionado?.canal?.tipoCanal === TipoCanalEnum.INSTAGRAM ||
    atendimentoSelecionado?.canal?.tipoCanal === TipoCanalEnum.MESSENGER;

  const tipoDeMensagemInexistente =
    !Object.values(TiposMensagemEnum).includes(tipo);

  const canReact =
    !fromMe &&
    isOpenSession &&
    !apagada &&
    !removidoPorAdminEm &&
    !isGroup &&
    !tipoDeMensagemInexistente &&
    !historicoAtendimento &&
    !historicoAtendimentoSelecionado &&
    !isInstagramOrMessengerChannel &&
    !historicoAtendimentoNaFila &&
    !atendimentoSelecionado?.contato?.bloqueadoEm;

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'messageMenuPopover',
  });

  const popupStateReacoes = usePopupState({
    variant: 'popover',
    popupId: 'emoji-popper',
  });

  const handleReagirMensagem = (reaction: string) => {
    popupStateReacoes.close();
    dispatch(reagirMensagem({ mensagemId: mensagem.id, reaction }));
  };

  const [showModalReacoes, hideModalReacoes] = useModal(
    ({ in: open }) =>
      open ? (
        <ModalReacoes
          open={open}
          onClose={hideModalReacoes}
          mensagemId={mensagem.id}
          desativarInteracao={tipoDeMensagemInexistente || !canReact}
        />
      ) : null,
    [mensagem.id],
  );

  const handleReenviarMensagem = useCallback(() => {
    setReenviando(true);
    setReenviado(true);
    if (onReenviarMensagem) onReenviarMensagem();

    let timeoutId: ReturnType<typeof setTimeout>;

    const handleStatus = () => {
      setReenviando(false);
      clearTimeout(timeoutId);
      socket.off(socketEvents.ON_MENSAGEM_STATUS, handleStatus);
    };

    socket.on(socketEvents.ON_MENSAGEM_STATUS, handleStatus);

    timeoutId = setTimeout(() => {
      setReenviando(false);
      socket.off(socketEvents.ON_MENSAGEM_STATUS, handleStatus);
    }, 15000);
  }, [onReenviarMensagem]);

  const renderStatusIcon = useMemo(() => {
    let StatusIcon = AccessTimeIcon;

    switch (ackStatus) {
      case 'ENVIADO':
        StatusIcon = DoneIcon;
        break;
      case 'RECEBIDO':
        StatusIcon = DoneAllIcon;
        break;
      case 'LIDO':
        StatusIcon = DoneAllIcon;
        break;
      default:
        StatusIcon = AccessTimeIcon;
        break;
    }

    if (ackMetaError) {
      StatusIcon = DoneIcon;
    }

    return (
      <StatusIcon
        fontSize="small"
        color={ackStatus === 'LIDO' ? 'primary' : 'disabled'}
      />
    );
  }, [ackMetaError, ackStatus]);

  const renderButtonReenviarMensagem = useMemo(() => {
    if (reenviado && !reenviando) return null;
    if (ackStatus !== 'ERRO' && !ackMetaError) return null;

    const tooltipTitle =
      ackMetaError && ackDescricaoErro
        ? ackDescricaoErro
        : 'Não foi possível enviar esta mensagem. Clique para reenviar';

    return (
      <Box pl={1} pr={1}>
        <Tooltip
          title={reenviando ? '' : tooltipTitle}
          arrow
          placement="top"
          classes={{ tooltip: classes.tooltipText }}
        >
          <span>
            <IconButton
              disabled={reenviando || ackMetaError || reenviado}
              onClick={
                ackMetaError
                  ? undefined
                  : e => {
                      e.stopPropagation();
                      handleReenviarMensagem();
                    }
              }
              color="secondary"
              edge="end"
            >
              {reenviando ? (
                <CircularProgress color="secondary" size={16} />
              ) : (
                <ErrorOutlineIcon
                  color={ackMetaError && ackDescricaoErro ? 'primary' : 'error'}
                  fontSize="small"
                />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }, [
    ackDescricaoErro,
    ackMetaError,
    ackStatus,
    classes.tooltipText,
    handleReenviarMensagem,
    reenviando,
    reenviado,
  ]);

  return (
    <ListItem
      onMouseEnter={
        evento || isSummaryIA ? undefined : () => setShowButtonReacoes(true)
      }
      onMouseLeave={
        evento || isSummaryIA
          ? undefined
          : () => !popupStateReacoes.isOpen && setShowButtonReacoes(false)
      }
      className={clsx(classes.root, {
        [classes.rootNaoIniciaAtendimento]: naoIniciaAtendimento,
        [classes.rootMensagemPesquisada]: showMensagemPesquisada,
      })}
      dense
    >
      <Container
        className={clsx(classes.container, {
          [classes.containerNaoIniciaAtendimento]: naoIniciaAtendimento,
        })}
        onMouseEnter={
          evento || isSummaryIA ? undefined : () => setShowMenuButton(true)
        }
        onMouseLeave={
          evento || isSummaryIA
            ? undefined
            : () => !popupStateReacoes.isOpen && setShowMenuButton(false)
        }
        maxWidth={false}
        disableGutters
      >
        {/* cabeçalho da mensagem */}
        {evento || isSummaryIA ? null : (
          <Box component="header" className={classes.header}>
            <Box pr={2} overflow="hidden" maxWidth="24ch" flexGrow={1}>
              <Typography
                title={autor}
                style={{ fontWeight: 'bold', color: autorColor || 'inherit' }}
                variant="caption"
                component="div"
                noWrap
              >
                {autor}
              </Typography>

              <Box fontStyle="italic">
                {/* mensagem encaminhada */}
                {encaminhada && !apagada && (
                  <Typography variant="caption" color="textSecondary">
                    Encaminhada
                  </Typography>
                )}

                {/* mensagem apagada */}
                {apagada && (
                  <Typography variant="caption" color="error">
                    Apagada
                  </Typography>
                )}
              </Box>

              {isEditada && (
                <ButtonBase
                  disabled={!onVerHistoricoMensagemEditada}
                  onClick={onVerHistoricoMensagemEditada}
                >
                  <Typography
                    variant="caption"
                    style={{ fontStyle: 'italic' }}
                    color="primary"
                  >
                    Editada
                  </Typography>
                </ButtonBase>
              )}
            </Box>

            <Zoom
              in={
                showMenuButton &&
                !apagada &&
                !removidoPorAdminEm &&
                Boolean(actions)
              }
            >
              <ButtonBase {...bindTrigger(popupState)} disableRipple>
                <MoreHorizIcon color="inherit" />
              </ButtonBase>
            </Zoom>

            {actions ? (
              <Menu
                {...bindMenu(popupState)}
                onClick={() => popupState.close()}
              >
                {actions}

                {canReact && (
                  <MenuItem {...bindTrigger(popupStateReacoes)}>
                    <ListItemIcon style={{ minWidth: 28 }}>
                      <EmojiEmotionsOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="body2">Reagir</Typography>
                  </MenuItem>
                )}
              </Menu>
            ) : null}
          </Box>
        )}

        {/* conteúdo da mensagem */}
        <Box position="relative">
          {naoIniciaAtendimento && (
            <Tooltip
              arrow
              placement="top"
              title="Esta mensagem não inicia atendimento."
              classes={{ tooltip: classes.tooltipText }}
            >
              <InfoOutlinedIcon
                className={classes.iconNaoIniciaAtendimento}
                fontSize="small"
              />
            </Tooltip>
          )}

          <Paper
            elevation={0}
            component="main"
            className={clsx(classes.contentMessage, {
              [classes.contentMessageNaoIniciaAtendimento]:
                naoIniciaAtendimento,
              [classes.contentMessageApagada]: apagada,
              [classes.contentMessageRemovida]: removidoPorAdminEm,
            })}
            // add background nos eventos que são do tipo FINALIZADO OU INICIADO
            style={{
              ...(evento?.tipo ===
                TiposEventoMensagemEnum.ATENDIMENTO_INICIADO && {
                background: alpha(
                  theme.palette.primary.main,
                  theme.palette.type === 'light' ? 0.2 : 0.3,
                ),
              }),
            }}
            onClick={onClick}
          >
            <Popover
              {...bindPopover(popupStateReacoes)}
              TransitionComponent={Fade}
            >
              <Paper className={classes.paper}>
                <EmojiPicker
                  categories={categories}
                  autoFocusSearch
                  previewConfig={{ showPreview: false }}
                  onEmojiClick={event => handleReagirMensagem(event.emoji)}
                  emojiStyle={EmojiStyle.NATIVE}
                  theme={
                    theme.palette.type === 'dark' ? Theme.DARK : Theme.LIGHT
                  }
                />
              </Paper>
            </Popover>

            {mensagemCitada && (
              <>
                {verMensagemCitadaNoModal ? (
                  <ResponseMessage
                    mensagem={mensagem}
                    mensagemCitada={mensagemCitada}
                    fromMe={fromMe}
                  />
                ) : (
                  <NewResponseMessage
                    mensagem={mensagem}
                    mensagemCitada={mensagemCitada}
                    fromMe={fromMe}
                    isGroup={isGroup}
                  />
                )}
              </>
            )}

            {removidoPorAdminEm ? (
              <MensagemMidiaRemovida
                tipoMidia={tipo}
                removidoPorAdminEm={removidoPorAdminEm}
                legenda={mensagem.midiaTitulo}
                isMe={false}
                contatosMencionados={mensagem.contatosMencionados}
              />
            ) : (
              children
            )}
          </Paper>
        </Box>

        {evento || isSummaryIA ? null : (
          <Box
            className={clsx(classes.footer, {
              [classes.flexEndFooter]: !mensagem.whatsappTemplateData,
            })}
            component="footer"
          >
            {/* Reações */}
            {reacoesEmojis &&
              Array.isArray(reacoesEmojis) &&
              reacoesEmojis.length > 0 && (
                <Box
                  flex={1}
                  style={{
                    marginTop: fromMe ? '-16px' : '-8px',
                    marginBottom: fromMe ? '0px' : '8px',
                    display: 'flex',
                    justifyContent: 'start',
                    paddingRight: '6px',
                  }}
                >
                  <Zoom in mountOnEnter unmountOnExit>
                    <Chip
                      clickable
                      onClick={showModalReacoes}
                      className={classes.chip}
                      color="secondary"
                      size="small"
                      label={
                        <Box color="inherit">
                          <Typography variant="inherit" color="inherit">
                            {reacoesEmojis
                              .map(emoji => (emoji === 'love' ? '🤎' : emoji))
                              .join(' ')}
                          </Typography>

                          {reacoesTotal > 1 && (
                            <Typography
                              style={{ marginLeft: '2px' }}
                              variant="inherit"
                            >
                              {reacoesTotal}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Zoom>
                </Box>
              )}

            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  mensagem.whatsappTemplateData ||
                  (isGroup && fromMe && mensagem.canal?.nome)
                    ? 'space-between'
                    : 'flex-end',
                flexGrow: 1,
                gap: 16,
              }}
            >
              {/* Nome do template */}
              {mensagem.whatsappTemplateData && (
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    overflow: 'hidden',
                  }}
                >
                  <Tooltip
                    arrow
                    placement="bottom"
                    title="Esta é uma mensagem de template do WhatsApp."
                  >
                    <SpeakerNotesOutlinedIcon
                      style={{ fontSize: 14, flexShrink: 0 }}
                      color="action"
                    />
                  </Tooltip>
                  <Typography
                    variant="inherit"
                    color="textSecondary"
                    noWrap
                    title={mensagem.whatsappTemplateData?.name}
                    style={{ maxWidth: 140 }}
                  >
                    {mensagem.whatsappTemplateData?.name}
                  </Typography>
                </Box>
              )}

              {/* Nome do canal (somente em grupos) */}
              {isGroup && fromMe && mensagem.canal?.nome && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  noWrap
                  style={{
                    maxWidth: 280,
                  }}
                >
                  {mensagem.canal?.nome}
                </Typography>
              )}

              {/* data da mensagem */}
              <Box style={{ display: 'flex', alignItems: 'center' }}>
                {mensagem.autor === 'ATENDENTE_EXTERNO' && (
                  <Tooltip
                    arrow
                    placement="bottom"
                    title="Esta mensagem foi enviada pelo celular ou pelo WhatsApp Web, fora da plataforma Tarelo."
                  >
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        marginRight: 6,
                      }}
                    >
                      <SmartphoneIcon style={{ fontSize: 14 }} color="action" />
                      <Typography variant="inherit" color="textSecondary">
                        Via WhatsApp
                      </Typography>
                    </Box>
                  </Tooltip>
                )}

                <Tooltip
                  arrow
                  placement="bottom"
                  title={dayjs(criadoEm).format(
                    'D [de] MMM [de] YYYY [às] HH:mm',
                  )}
                >
                  <Typography variant="inherit" color="textSecondary">
                    {dayjs(criadoEm).format('HH:mm')}
                  </Typography>
                </Tooltip>

                {/* icone sinalizando o status de recebimento da mensagem */}
                {fromMe && !ackMetaError && (
                  <Box marginLeft="3px">{renderStatusIcon}</Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Container>

      {canReact && showButtonReacoes && (
        <ButtonBase
          style={{ marginLeft: 8 }}
          disableRipple
          {...bindTrigger(popupStateReacoes)}
        >
          <EmojiEmotionsOutlinedIcon color="action" />
        </ButtonBase>
      )}

      {evento || isSummaryIA ? null : renderButtonReenviarMensagem}
    </ListItem>
  );
}
